import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Sidebar, PageHeader, formatCurrency, CURRENCIES } from "@/components/Layout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  User, 
  Crown, 
  CreditCard, 
  Check,
  DollarSign,
  Sparkles,
  Clock,
  Zap,
  Loader2,
  Settings,
  ExternalLink,
  HelpCircle,
  PlayCircle,
  RefreshCw,
  AlertCircle
} from "lucide-react";

const SUBSCRIPTION_PLANS = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 4.99,
    currency: 'GBP',
    period: 'month',
    features: [
      'AI-powered insights',
      'Advanced scenario planning',
      'Predictive forecasting',
      'Premium education content',
      'Priority support'
    ]
  },
  {
    id: 'annual',
    name: 'Annual',
    price: 39.99,
    currency: 'GBP',
    period: 'year',
    savings: '£19.89',
    monthlyEquivalent: 3.33,
    features: [
      'All monthly features',
      'Save £19.89 per year',
      'Early access to new features',
      'Annual financial review'
    ]
  }
];

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [trialStatus, setTrialStatus] = useState(null);
  const [startingTrial, setStartingTrial] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [syncError, setSyncError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    currency: user?.currency || 'GBP'
  });

  // Fetch trial status on mount
  useEffect(() => {
    fetchTrialStatus();
  }, []);

  const fetchTrialStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/trial-status`, { withCredentials: true });
      setTrialStatus(response.data);
    } catch (error) {
      console.error("[Settings] Error fetching trial status:", error);
    }
  };

  // SUBSCRIPTION DISPLAY PRIORITY LOGIC:
  // Priority 1: Stripe subscription (active/trialing) = Premium
  // Priority 2: Internal trial active = Trial
  // Priority 3: Free

  const isStripePremium = () => {
    if (!trialStatus) return false;
    
    // Check subscription_source first (new field)
    if (trialStatus.subscription_source === 'stripe' && 
        ['active', 'trialing'].includes(trialStatus.subscription_status)) {
      return true;
    }
    
    // Fallback to checking stripe_subscription_id
    if (trialStatus.stripe_subscription_id && 
        ['active', 'trialing'].includes(trialStatus.subscription_status)) {
      return true;
    }
    
    return false;
  };

  const isInternalTrial = () => {
    if (!trialStatus) return false;
    
    // Not internal trial if we have Stripe subscription
    if (isStripePremium()) return false;
    
    // Check internal_trial_active (new field)
    if (trialStatus.internal_trial_active === true) {
      return true;
    }
    
    // Fallback to legacy fields
    if (trialStatus.plan_type === 'trial' && 
        !trialStatus.trial_expired && 
        !trialStatus.stripe_subscription_id) {
      return true;
    }
    
    return false;
  };

  const isFree = () => {
    return !isStripePremium() && !isInternalTrial();
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      const response = await axios.post(`${API}/subscription/start-trial`, {}, { withCredentials: true });
      toast.success(`🎉 Trial started! You have ${response.data.trial_days} days of premium access.`);
      await fetchTrialStatus();
      updateUser({ is_premium: true, plan_type: "trial" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not start trial");
    } finally {
      setStartingTrial(false);
    }
  };

  // Check for payment success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error('Payment was cancelled');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment verification timed out. Try clicking "Sync Subscription".');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, { withCredentials: true });
      
      if (response.data.payment_status === 'paid') {
        toast.success('Welcome to Premium! Your account has been upgraded.');
        // Refresh both trial status and user data
        await fetchTrialStatus();
        if (refreshUser) await refreshUser();
        setCheckingPayment(false);
        window.history.replaceState({}, '', '/settings');
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        setCheckingPayment(false);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/users/settings`, formData, { withCredentials: true });
      updateUser(response.data);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/create-checkout-session`, {
        plan: selectedPlan,
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/create-portal-session`, {
        return_url: window.location.href
      }, { withCredentials: true });
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Portal error:', error);
      toast.error(error.response?.data?.detail || 'Failed to open subscription management');
      setPortalLoading(false);
    }
  };

  const handleSyncSubscription = async () => {
    setSyncLoading(true);
    setSyncSuccess(false);
    setSyncError(null);
    
    try {
      const response = await axios.post(`${API}/subscription/sync`, {}, { withCredentials: true });
      
      if (response.data.synced) {
        if (response.data.subscription_status && ['active', 'trialing'].includes(response.data.subscription_status)) {
          setSyncSuccess(true);
          toast.success('Subscription Active');
          
          // Update user context with new premium status
          updateUser({ 
            is_premium: response.data.is_premium,
            plan_type: response.data.plan_type,
            stripe_customer_id: response.data.stripe_customer_id,
            stripe_subscription_id: response.data.stripe_subscription_id,
            subscription_status: response.data.subscription_status
          });
          
          // Refresh user data from server
          if (refreshUser) await refreshUser();
        } else {
          toast.info(response.data.message);
        }
        
        // Refresh trial status to update UI
        await fetchTrialStatus();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('[Settings] Sync error:', error);
      setSyncError(true);
      toast.error("We couldn't verify your subscription yet. Please try again.");
    } finally {
      setSyncLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  // Get subscription status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-emerald-400';
      case 'trialing': return 'text-blue-400';
      case 'past_due': return 'text-amber-400';
      case 'canceled': return 'text-red-400';
      default: return 'text-p2p-muted';
    }
  };

  // Get subscription status display text
  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'trialing': return 'Trial';
      case 'past_due': return 'Past Due';
      case 'canceled': return 'Canceled';
      default: return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown';
    }
  };

  return (
    <Sidebar>
      <PageHeader 
        title="Settings"
        subtitle="Manage your account and preferences"
      />

      {checkingPayment && (
        <div className="glass-card p-6 rounded-2xl mb-6 flex items-center gap-4 border border-p2p-gold/20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-p2p-gold"></div>
          <div>
            <p className="font-medium text-p2p-gray">Verifying your payment...</p>
            <p className="text-sm text-p2p-muted">This may take a moment</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Profile Settings */}
        <div className="glass-card p-6 rounded-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-p2p-gold/20 flex items-center justify-center">
              <User className="w-5 h-5 text-p2p-gold" />
            </div>
            <h2 className="font-outfit text-lg font-semibold text-p2p-gray">Profile Settings</h2>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-p2p-muted">Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="input-dark"
                data-testid="settings-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-p2p-muted">Email</Label>
              <Input
                value={user?.email}
                disabled
                className="input-dark opacity-60"
              />
              <p className="text-xs text-p2p-muted">Email cannot be changed</p>
            </div>

            <div className="space-y-2">
              <Label className="text-p2p-muted">Preferred Currency</Label>
              <Select 
                value={formData.currency} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, currency: val }))}
              >
                <SelectTrigger className="input-dark" data-testid="settings-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-p2p-teal border-white/10">
                  {Object.entries(CURRENCIES).map(([code, { symbol, name }]) => (
                    <SelectItem key={code} value={code} className="text-p2p-gray hover:bg-white/10">
                      {symbol} {name} ({code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button 
              className="w-full btn-gold mt-4"
              onClick={handleSaveSettings}
              disabled={loading}
              data-testid="save-settings-btn"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
            
            {/* View Guide Button */}
            <Button 
              variant="outline"
              className="w-full mt-3 border-p2p-border/50 text-p2p-gray hover:bg-p2p-surface"
              onClick={() => navigate('/calendar?guide=1')}
              data-testid="view-guide-btn"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              View Dashboard Guide
            </Button>
          </div>
        </div>

        {/* Subscription */}
        <div className="glass-card p-6 rounded-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-p2p-gold/20 flex items-center justify-center">
              <Crown className="w-5 h-5 text-p2p-gold" />
            </div>
            <h2 className="font-outfit text-lg font-semibold text-p2p-gray">Subscription</h2>
          </div>

          {/* PRIORITY 1: Stripe Premium (active or trialing) */}
          {isStripePremium() ? (
            <div className="text-center py-6" data-testid="subscription-premium">
              <div className="w-16 h-16 rounded-full bg-p2p-gold/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-p2p-gold" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-p2p-gray mb-2">
                {trialStatus?.subscription_status === 'trialing' ? 'Premium Trial Active' : 'Premium Member'}
              </h3>
              <p className="text-p2p-muted mb-2">
                {trialStatus?.subscription_status === 'trialing' 
                  ? `Your subscription will begin billing on ${formatDate(trialStatus?.trial_end)}.`
                  : 'You have access to all premium features'}
              </p>
              
              {/* Subscription Details Card */}
              <div className="glass-card p-4 rounded-xl bg-p2p-gold/5 border border-p2p-gold/20 mb-4 text-left">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-p2p-muted">Status</span>
                  <span className={`font-medium ${getStatusColor(trialStatus?.subscription_status)}`}>
                    {getStatusText(trialStatus?.subscription_status)}
                  </span>
                </div>
                
                {trialStatus?.billing_interval && (
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-p2p-muted">Plan</span>
                    <span className="text-p2p-gray capitalize">
                      {trialStatus.selected_plan || trialStatus.billing_interval}ly
                    </span>
                  </div>
                )}
                
                {trialStatus?.subscription_status === 'trialing' && trialStatus?.trial_end && (
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-p2p-muted">Billing Starts</span>
                    <span className="text-p2p-gray">{formatDate(trialStatus.trial_end)}</span>
                  </div>
                )}
                
                {trialStatus?.subscription_status === 'active' && (trialStatus?.subscription_end_date || trialStatus?.current_period_end) && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-p2p-muted">
                      {trialStatus?.cancel_at_period_end ? 'Access Until' : 'Renews'}
                    </span>
                    <span className="text-p2p-gray">
                      {formatDate(trialStatus.subscription_end_date || trialStatus.current_period_end)}
                    </span>
                  </div>
                )}
                
                {trialStatus?.cancel_at_period_end && (
                  <div className="mt-2 p-2 rounded bg-amber-500/10 border border-amber-500/20">
                    <p className="text-xs text-amber-400">
                      Your subscription will not renew. Access continues until the end date.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Manage Subscription Button */}
              {trialStatus?.stripe_customer_id && (
                <Button
                  variant="outline"
                  className="w-full rounded-xl border-p2p-gold/30 text-p2p-gold hover:bg-p2p-gold/10"
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  data-testid="manage-subscription-btn"
                >
                  {portalLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Settings className="w-4 h-4 mr-2" />
                  )}
                  Manage Subscription
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
              )}
            </div>
          ) : isInternalTrial() ? (
            /* PRIORITY 2: Internal Trial Active (no Stripe subscription) */
            <div className="text-center py-6" data-testid="subscription-trial">
              <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-p2p-gray mb-2">Trial Active</h3>
              <p className="text-p2p-muted mb-2">
                {trialStatus?.trial_days_remaining || 0} days remaining
              </p>
              
              {(trialStatus?.trial_days_remaining || 0) <= 3 && (
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-4">
                  <p className="text-sm text-amber-400">
                    Your trial ends soon! Subscribe to keep premium features.
                  </p>
                </div>
              )}
              
              <Button 
                className="btn-gold rounded-full px-8"
                onClick={() => setUpgradeDialogOpen(true)}
                data-testid="open-upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" /> Subscribe Now
              </Button>
              
              {/* Sync Button for Internal Trial users */}
              <div className="mt-4 pt-4 border-t border-p2p-border/30">
                <p className="text-xs text-p2p-muted mb-2">Already subscribed via Stripe?</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-p2p-muted hover:text-p2p-gray ${syncSuccess ? 'text-emerald-400' : ''} ${syncError ? 'text-red-400' : ''}`}
                  onClick={handleSyncSubscription}
                  disabled={syncLoading}
                  data-testid="sync-subscription-btn"
                >
                  {syncLoading ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin mr-2" />
                      Syncing...
                    </>
                  ) : syncSuccess ? (
                    <>
                      <Check className="w-3 h-3 mr-2" />
                      Subscription Active
                    </>
                  ) : syncError ? (
                    <>
                      <AlertCircle className="w-3 h-3 mr-2" />
                      Try Again
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3 h-3 mr-2" />
                      Sync Subscription
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            /* PRIORITY 3: Free Plan */
            <div className="text-center py-6" data-testid="subscription-free">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-p2p-muted" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-p2p-gray mb-2">Free Plan</h3>
              <p className="text-p2p-muted mb-6">Upgrade to unlock AI insights and advanced features</p>
              
              {/* Trial CTA - only show if can start trial */}
              {trialStatus?.can_start_trial && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 mb-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-amber-400" />
                    <span className="font-semibold text-white">Try Premium Free for 14 Days</span>
                  </div>
                  <p className="text-sm text-slate-400 mb-3">Card required • Cancel anytime before trial ends</p>
                  <Button
                    onClick={handleStartTrial}
                    disabled={startingTrial}
                    className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
                    data-testid="start-trial-btn"
                  >
                    {startingTrial ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Zap className="w-4 h-4 mr-2" />
                    )}
                    Start Free Trial
                  </Button>
                </div>
              )}
              
              <Button 
                className="btn-gold rounded-full px-8"
                onClick={() => setUpgradeDialogOpen(true)}
                data-testid="open-upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" /> Upgrade to Premium
              </Button>
              
              {/* Sync Button for Free users (in case webhook failed) */}
              <div className="mt-4 pt-4 border-t border-p2p-border/30">
                <p className="text-xs text-p2p-muted mb-2">Already subscribed via Stripe?</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-p2p-muted hover:text-p2p-gray ${syncSuccess ? 'text-emerald-400' : ''} ${syncError ? 'text-red-400' : ''}`}
                  onClick={handleSyncSubscription}
                  disabled={syncLoading}
                  data-testid="sync-subscription-btn"
                >
                  {syncLoading ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin mr-2" />
                      Syncing...
                    </>
                  ) : syncSuccess ? (
                    <>
                      <Check className="w-3 h-3 mr-2" />
                      Subscription Active
                    </>
                  ) : syncError ? (
                    <>
                      <AlertCircle className="w-3 h-3 mr-2" />
                      Try Again
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3 h-3 mr-2" />
                      Sync Subscription
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen} modal={true}>
        <DialogContent className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700 text-white max-w-lg p-0 overflow-hidden">
          {/* Header */}
          <div className="p-6 pb-4 text-center border-b border-slate-700">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mx-auto mb-4">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <DialogHeader>
              <DialogTitle className="font-outfit text-2xl font-bold text-white">
                Take control of your money
              </DialogTitle>
            </DialogHeader>
            <p className="text-slate-400 mt-2">
              Stop guessing. Start knowing exactly what to do.
            </p>
          </div>

          {/* Benefits */}
          <div className="px-6 py-5 space-y-3">
            {[
              "See your finances 6 months ahead",
              "Get exact steps to avoid shortfalls",
              "Pay off debt faster with smart simulations",
              "Increase your safe-to-spend"
            ].map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-slate-200">{benefit}</span>
              </div>
            ))}
          </div>

          {/* Plan Selection */}
          <div className="px-6 pb-4">
            <div className="grid grid-cols-2 gap-3">
              {/* Monthly Plan */}
              <div 
                className={`p-4 rounded-xl cursor-pointer transition-all border-2 ${
                  selectedPlan === 'monthly' 
                    ? 'bg-slate-700/50 border-slate-500' 
                    : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                }`}
                onClick={() => setSelectedPlan('monthly')}
                data-testid="plan-monthly"
              >
                <p className="text-sm text-slate-400 mb-1">Monthly</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-white">£4.99</span>
                  <span className="text-slate-500 text-sm">/mo</span>
                </div>
              </div>
              
              {/* Annual Plan */}
              <div 
                className={`p-4 rounded-xl cursor-pointer transition-all border-2 relative ${
                  selectedPlan === 'annual' 
                    ? 'bg-emerald-500/10 border-emerald-500' 
                    : 'bg-slate-800/50 border-slate-700 hover:border-emerald-500/50'
                }`}
                onClick={() => setSelectedPlan('annual')}
                data-testid="plan-annual"
              >
                <span className="absolute -top-2 right-2 px-2 py-0.5 bg-emerald-500 text-white text-xs font-medium rounded-full">
                  Best Value
                </span>
                <p className="text-sm text-slate-400 mb-1">Annual</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-white">£39.99</span>
                  <span className="text-slate-500 text-sm">/yr</span>
                </div>
                <p className="text-xs text-emerald-400 mt-1">Save £19.89 · Just £3.33/mo</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="px-6 pb-6 space-y-3">
            <Button 
              className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold text-base rounded-xl"
              onClick={handleUpgrade}
              disabled={checkoutLoading}
              data-testid="confirm-upgrade-btn"
            >
              {checkoutLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Opening checkout...
                </>
              ) : (
                'Subscribe Now'
              )}
            </Button>
            
            <p className="text-center text-xs text-slate-500">
              Card required • Cancel anytime before trial ends
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
